import React, { useState, useEffect, useCallback } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Box,
  Alert,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  TableContainer,
  Paper,
  Button
} from '@mui/material';
import { recalculatePriorities, dedupPendingDomains, deletePendingDomain } from '../../../services/api';
import {
  usePendingDomains,
  useHypeStatScraper,
  useFallbackConfig,
  useScrapAudience,
  useEnrichProgress
} from '../../../hooks/admin';
import {
  HeaderSection,
  StatsSection,
  FiltersSection,
  ScrapingToolsSection,
  DomainsTable,
  DialogsManager,
  LoadingSkeleton,
  SyncResultAlert,
  EmptyState
} from './PendingDomainsPanel/sections';
import HypeStatResultsDialog from './dialogs/HypeStatResultsDialog';
import DeletePendingDomainDialog from './dialogs/DeletePendingDomainDialog';

const PendingDomainsPanel = () => {
  const theme = useMuiTheme();

  // === HOOKS PERSONALIZADOS ===
  const pendingDomains = usePendingDomains();

  // Callback para atualizar stats E lista de domínios após scraping
  const onHypeStatComplete = useCallback(() => {
    pendingDomains.fetchStats();
    pendingDomains.fetchDomains();
  }, [pendingDomains.fetchStats, pendingDomains.fetchDomains]);

  const hypestat = useHypeStatScraper(onHypeStatComplete);
  const fallback = useFallbackConfig();
  const scrapAudience = useScrapAudience();

  // Hook de Progresso do Enriquecimento
  const enrich = useEnrichProgress((result) => {
    // Callback quando termina
    setSnackbar({
      open: true,
      message: `Enriquecimento concluído: ${result.successCount} sucessos, ${result.errorCount} erros`,
      severity: result.errorCount > 0 ? 'warning' : 'success'
    });
    fetchDomains(); // Atualiza lista
  });

  // Performance: Set para lookup O(1) ao invés de array.includes O(n)
  const selectedDomainsSet = React.useMemo(
    () => new Set(hypestat.selectedDomains),
    [hypestat.selectedDomains]
  );

  // Desestruturar do hook de domains
  const {
    domains,
    loading,
    stats,
    views,
    viewCounts,
    selectedView,
    handleViewChange,
    statusFilter,
    setStatusFilter,
    showCompleted,
    setShowCompleted,
    priorityFilter,
    setPriorityFilter,
    sortBy,
    sortDirection,
    searchInput,
    currentPage,
    totalPages,
    totalDomains,
    itemsPerPage,
    setItemsPerPage,
    syncLoading,
    syncResult,
    fetchDomains,
    fetchStats,
    fetchViewCounts,
    handleSearchChange,
    handlePageChange,
    handleSort,
    processDomain,
    ignoreDomain,
    fetchDomain,
    syncDomains,
    filteredIds,
    setFilteredIds,
    zeroedValuationFilter,
    setZeroedValuationFilter,
    enrichSingleDomain,
    enrichMultipleDomains,
    audienceSourceFilter,
    setAudienceSourceFilter
  } = pendingDomains;

  // === ESTADOS ESPECÍFICOS DE UI (não no hook) ===
  const [error, setError] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  // processData removido, agora gerenciado internamente pelo DomainEditDialog

  // Estado para Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Estados para diálogos de exportação e importação
  const [filterTypeDialog, setFilterTypeDialog] = useState(false);
  const [exportFilterType, setExportFilterType] = useState('all');
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [scrapAudienceDialogOpen, setScrapAudienceDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');

  // === CALLBACKS MEMOIZADOS PARA DomainRow ===
  const handleVisitDomain = useCallback((domain) => {
    const url = domain.domain.startsWith('http') ? domain.domain : `https://${domain.domain}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleProcessDomain = useCallback((domain) => {
    setSelectedDomain(domain);
    setProcessDialogOpen(true);
  }, []);

  const handleProcessSubmit = async (data) => {
    const result = await processDomain(selectedDomain._id, data);
    if (result.success) {
      setProcessDialogOpen(false);

      // Mostrar feedback sobre artigos atualizados
      if (result.articlesUpdated > 0) {
        setSnackbar({
          open: true,
          message: `Domínio processado! ${result.articlesUpdated} artigo(s) atualizado(s) com valoração.`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Domínio processado com sucesso!',
          severity: 'success'
        });
      }
    } else {
      setError(result.error);
    }
  };

  const handleIgnoreDomainLocal = useCallback(async (domain) => {
    const result = await ignoreDomain(domain._id);
    if (!result.success) {
      setError(result.error);
    }
  }, [ignoreDomain]);

  const handleFetchDataLocal = useCallback(async (domain) => {
    const result = await fetchDomain(domain._id);
    if (!result.success) {
      setError(result.error);
    }
  }, [fetchDomain]);

  const handleEnrichDomain = useCallback(async (domain) => {
    const result = await enrichSingleDomain(domain._id);
    if (result.success) {
      setSnackbar({
        open: true,
        message: `Domínio enriquecido: ${result.data.siteName || domain.domain}`,
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Erro ao enriquecer domínio',
        severity: 'error'
      });
    }
  }, [enrichSingleDomain]);

  const handleEnrichBatch = useCallback(async () => {
    if (hypestat.selectedDomains.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione pelo menos um domínio para enriquecer',
        severity: 'warning'
      });
      return;
    }

    // Buscar IDs dos domínios selecionados
    const selectedIds = domains
      .filter(d => hypestat.selectedDomains.includes(d.registrableDomain || d.domain))
      .map(d => d._id);

    // Iniciar Batch via Hook
    const result = await enrich.startBatch(selectedIds);

    if (!result.success) {
      setSnackbar({
        open: true,
        message: result.error || 'Erro ao iniciar enriquecimento',
        severity: 'error'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Enriquecimento em massa iniciado em background',
        severity: 'info'
      });
    }
  }, [hypestat, domains, enrich]);

  const handleSyncDomainsLocal = async () => {
    const result = await syncDomains();
    if (result.success) {
      // Atualizar contagens das views após sincronização
      fetchViewCounts();
    } else {
      setError(result.error);
    }
  };



  // === FALLBACK CONFIG HANDLERS ===
  const handleUpdatePercentile = async () => {
    const result = await fallback.updatePercentile();
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleRecalculateFallback = async () => {
    const result = await fallback.recalculate();
    if (result.success) {
      setSnackbar({ open: true, message: result.message, severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  // === RECÁLCULO DE PRIORIDADES ===
  const [priorityRecalcLoading, setPriorityRecalcLoading] = useState(false);

  const handleRecalculatePriorities = async () => {
    try {
      setPriorityRecalcLoading(true);
      const response = await recalculatePriorities();
      const dist = response.data.data.distribution;
      setSnackbar({
        open: true,
        message: `Prioridades recalculadas! Urgente: ${dist.urgente}, Alta: ${dist.alta}, Normal: ${dist.normal}, Baixa: ${dist.baixa}, Mínima: ${dist.minima}`,
        severity: 'success'
      });
      // Recarregar lista para mostrar novas prioridades
      fetchDomains();
      fetchStats(); // Atualizar estatísticas
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao recalcular prioridades: ' + error.message, severity: 'error' });
    } finally {
      setPriorityRecalcLoading(false);
    }
  };


  // Carrega estatísticas ao montar
  useEffect(() => {
    fetchStats();
  }, []);

  // === SCRAP AUDIENCE HANDLERS ===
  const handleExportLinks = () => {
    setFilterTypeDialog(true);
  };

  const handleExportConfirm = async () => {
    // Se tiver itens selecionados, exportar apenas eles
    let selectedIds = null;

    if (hypestat.selectedDomains && hypestat.selectedDomains.length > 0) {
      // Mapear domínios selecionados (strings) para IDs reais
      // O hypestat.selectedDomains contém registrableDomains ou domains (strings)
      selectedIds = domains
        .filter(d => hypestat.selectedDomains.includes(d.registrableDomain || d.domain))
        .map(d => d._id);

      console.log(`📤 Exportando ${selectedIds.length} domínios selecionados manualmente.`);
    }

    const result = await scrapAudience.exportDomains(selectedIds);
    if (!result.success) {
      setError(result.error);
    }
  };

  const handleImportAudienceClick = async () => {
    const result = await scrapAudience.openImportDialog();
    if (!result.success) {
      setError(result.error);
    }
  };

  const handleImportScraperFile = async () => {
    const result = await scrapAudience.importFile();
    if (result.success) {
      fetchDomains();
    } else {
      setError(result.error);
    }
  };

  const handleBulkImport = async (urls) => {
    const response = await pendingDomains.importUrlsBatch(urls);
    if (response.success && response.data?.ids?.length > 0) {
      setFilteredIds(response.data.ids);
      // Resetar filtros para garantir que os importados apareçam
      setStatusFilter('all');
      setPriorityFilter('');

      setSnackbar({
        open: true,
        message: `Filtrando ${response.data.ids.length} domínios importados`,
        severity: 'info'
      });
    }
    return response;
  };

  const handleDedup = async (dryRun) => {
    return dedupPendingDomains(dryRun);
  };

  // === DELETE DOMAIN ===
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Abre o dialog de confirmação
  const handleDeleteDomain = (domain) => {
    setDomainToDelete(domain);
  };

  // Confirma e executa a exclusão
  const handleDeleteConfirm = async () => {
    if (!domainToDelete) return;
    setDeleteLoading(true);
    try {
      await deletePendingDomain(domainToDelete._id);
      setSnackbar({ open: true, message: `Domínio "${domainToDelete.domain}" excluído com sucesso.`, severity: 'success' });
      setDomainToDelete(null);
      fetchDomains();
      fetchStats();
    } catch (err) {
      setSnackbar({ open: true, message: `Erro ao excluir: ${err.response?.data?.message || err.message}`, severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  // getPriorityColor e getPriorityLabel agora vêm de domainUtils


  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box sx={{ pt: 3, pr: 3, pb: 3, pl: 3 }}>
      <HeaderSection
        onExport={handleExportLinks}
        onImport={handleImportAudienceClick}
        onBulkImportClick={() => setBulkImportOpen(true)}
        onSync={handleSyncDomainsLocal}
        onRefresh={() => { fetchDomains(); fetchStats(); }}
        syncLoading={syncLoading}
        onEnrichBatch={handleEnrichBatch}
        selectedCount={hypestat.selectedDomains.length}
        enrichLoading={enrichLoading}
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        selectedView={selectedView}
        views={views}
      />

      <StatsSection
        stats={stats}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
      />

      <FiltersSection
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        processedAtFilter={pendingDomains.processedAtFilter}
        setProcessedAtFilter={pendingDomains.setProcessedAtFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        showCompleted={showCompleted}
        setShowCompleted={setShowCompleted}
        enrichedByFilter={pendingDomains.enrichedByFilter}
        setEnrichedByFilter={pendingDomains.setEnrichedByFilter}
        audienceSourceFilter={audienceSourceFilter}
        setAudienceSourceFilter={setAudienceSourceFilter}
        advancedFilters={pendingDomains.advancedFilters}
        setAdvancedFilters={pendingDomains.setAdvancedFilters}
        zeroedValuationFilter={zeroedValuationFilter}
        setZeroedValuationFilter={setZeroedValuationFilter}
        views={views}
        viewCounts={viewCounts}
        selectedView={selectedView}
        onViewChange={handleViewChange}
        showSearch={false}
      />

      <ScrapingToolsSection
        hypestat={hypestat}
        fallback={fallback}
        domains={domains}
        onUpdatePercentile={handleUpdatePercentile}
        onRecalculateFallback={handleRecalculateFallback}
        onRecalculatePriorities={handleRecalculatePriorities}
        priorityRecalcLoading={priorityRecalcLoading}
        onDedup={handleDedup}
        enrichProgress={enrich.progress}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SyncResultAlert syncResult={syncResult} />

      {filteredIds && filteredIds.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setFilteredIds([])}>
              Limpar Filtro
            </Button>
          }
        >
          Filtrando por <strong>{filteredIds.length}</strong> domínios importados
        </Alert>
      )}

      {domains.length === 0 ? (
        <EmptyState />
      ) : (
        <DomainsTable
          domains={domains}
          selectedDomainsSet={selectedDomainsSet}
          hypestat={hypestat}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
          onVisitDomain={handleVisitDomain}
          onFetchData={handleFetchDataLocal}
          onProcessDomain={handleProcessDomain}
          onIgnoreDomain={handleIgnoreDomainLocal}
          onEnrichDomain={handleEnrichDomain}
          onDeleteDomain={handleDeleteDomain}
          currentPage={currentPage}
          totalPages={totalPages}
          totalDomains={totalDomains}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      <DialogsManager
        fallback={fallback}
        onUpdatePercentile={handleUpdatePercentile}
        filterTypeDialog={filterTypeDialog}
        setFilterTypeDialog={setFilterTypeDialog}
        exportFilterType={exportFilterType}
        setExportFilterType={setExportFilterType}
        exportFormat={scrapAudience.exportFormat}
        setExportFormat={scrapAudience.setExportFormat}
        onExportConfirm={handleExportConfirm}
        instructionsDialogOpen={instructionsDialogOpen}
        setInstructionsDialogOpen={setInstructionsDialogOpen}
        scrapAudienceDialogOpen={scrapAudienceDialogOpen}
        setScrapAudienceDialogOpen={setScrapAudienceDialogOpen}
        availableFiles={availableFiles}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        onImportFile={handleImportScraperFile}
        bulkImportOpen={bulkImportOpen}
        setBulkImportOpen={setBulkImportOpen}
        onBulkImport={handleBulkImport}
        processDialogOpen={processDialogOpen}
        setProcessDialogOpen={setProcessDialogOpen}
        selectedDomain={selectedDomain}
        onProcessSubmit={handleProcessSubmit}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
      />

      {/* Modal de Confirmação de Exclusão */}
      <DeletePendingDomainDialog
        open={!!domainToDelete}
        domain={domainToDelete}
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
        onClose={() => !deleteLoading && setDomainToDelete(null)}
      />

      {/* Modal de Resultados do HypeStat */}
      <HypeStatResultsDialog
        open={!!hypestat.scrapingResults}
        onClose={hypestat.clearResults}
        results={hypestat.scrapingResults}
      />
    </Box>
  );
};

export default PendingDomainsPanel;
